import { useRef, useState } from "react";
import dompurify from "dompurify";
import "./SignaturePreview.css";

const SignaturePreview = ({ userData, lang  }) => {
  const signatureRef = useRef(null);
  const [isCopied, setIsCopied] = useState(false);
  const [useIcon, setUseIcon] = useState(true);
  const [fontSize, setFontSize] = useState(10.5);

  const firstName = userData.firstName?.[lang] || "";
  const lastName = userData.lastName?.[lang] || "";
  const position = userData.position?.[lang] || "";
  
  const copyToClipboard = () => {
    if (!signatureRef.current) return;
    const html = signatureRef.current.innerHTML;

    const listener = (e) => {
      e.clipboardData.setData("text/html", html);
      e.clipboardData.setData("text/plain", signatureRef.current.innerText);
      e.preventDefault();
    };
    document.addEventListener("copy", listener);
    document.execCommand("copy");
    document.removeEventListener("copy", listener);

    setIsCopied(true);
    setTimeout(() => setIsCopied(false), 2000);
  };

  const sanitizeHtml = (html) => {
    return dompurify.sanitize(html);
  };

  let fullWebsiteUrl;
  if (userData.companyWebsite.startsWith("http")) {
    fullWebsiteUrl = userData.companyWebsite;
  } else {
    fullWebsiteUrl = `https://${userData.companyWebsite}`;
  }

    const getContactText = (type) => {
    if (useIcon) {
      return { mobile: "📱", work: "☎️", email: "✉️", website: "🌐" }[type];
    } else {
      if (lang === "Русский") {
        return { mobile: "Моб:", work: "Раб:", email: "Почта:", website: "Сайт:" }[type];
      }
      return { mobile: "Cell:", work: "Work:", email: "Email:", website: "Website:" }[type];
    }
  };

  const getWorkPhone = () => {
    let phone = userData.workPhoneMain;
    if (userData.workPhoneExt) {
      const ext = lang === "Русский" ? " доб." : " ext.";
      phone += ext;
      phone += userData.workPhoneExt;
    }
    return phone;
  };

  const signatureHtml = sanitizeHtml(`
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="utf-8"/>
      <title>Signature</title>
    </head>
    <body>
      <table cellpadding="0" cellspacing="0" border="0" style="border-collapse:collapse;">
        <tr>
          <!-- Фото -->
          <td valign="top" style="padding:0 10px 0 0; border:none;">
            <img src="${
              userData.combinedImageUrl
            }" height="${userData.combinedImageHeight}" style="display:block;"/>
          </td>

          <!-- Правая колонка -->
          <td width="550" height="${userData.combinedImageHeight}" valign="top" style="padding:0; border:none;">
            <table width="100%" cellpadding="0" cellspacing="0" border="0" style="border-collapse:collapse;">
              <tr>
                <td valign="top" height="60" style="padding:0;">
                  <!-- Верхний блок: имя и должность -->
                  <table width="100%" cellpadding="0" cellspacing="0" border="0">
                    <tr>
                      <td style="padding:0;">
                        <p style="margin:0; font-family:'Times New Roman', serif; font-size:${fontSize}pt; line-height:140%;">
                          ${lang === "Русский" ? "С уважением" : "Best regards"}, 
                          <b>${firstName} ${lastName}</b><br/>
                          ${position}
                        </p>
                      </td>
                    </tr>
                  </table>
                </td>
              </tr>

              <!-- Разделитель — растягивается -->
              <tr>
                <td height="100%" style="font-size:0; line-height:0;">&nbsp;</td>
              </tr>

              <tr>
                <td valign="bottom" height="${userData.combinedImageHeight - 60}" style="padding:0;">
                  <!-- Нижний блок: контакты -->
                  <table width="100%" cellpadding="0" cellspacing="0" border="0">
                    <tr>
                      <td style="padding:0;">
                        <p style="margin:0; font-family:'Times New Roman', serif; font-size:${fontSize}pt; line-height:140%;">
                          ${
                            userData.mobilePhone
                              ? `${getContactText("mobile")} <a href="tel:${
                                  userData.mobilePhone
                                }" style="color:#0066cc; text-decoration:none;">${
                                  userData.mobilePhone
                                }</a><br/>`
                              : ""
                          }
                          ${
                            userData.workPhoneMain
                              ? `${getContactText(
                                  "work"
                                )} <a href="tel:${getWorkPhone()}" style="color:#0066cc; text-decoration:none;">${getWorkPhone()}</a><br/>`
                              : ""
                          }
                          ${
                            userData.email
                              ? `${getContactText("email")} <a href="mailto:${
                                  userData.email
                                }" style="color:#0066cc; text-decoration:none;">${
                                  userData.email
                                }</a><br/>`
                              : ""
                          }
                          ${
                            userData.companyWebsite
                              ? `${getContactText(
                                  "website"
                                )} <a href="${fullWebsiteUrl}" style="color:#0066cc; text-decoration:none;">${
                                  userData.companyWebsite
                                }</a>`
                              : ""
                          }
                        </p>
                      </td>
                    </tr>
                  </table>
                </td>
              </tr>
            </table>
          </td>
        </tr>
      </table>
    </body>
    </html>
  `);

  const increaseFont = () => setFontSize((prev) => Math.min(prev + 0.5, 15));
  const decreaseFont = () => setFontSize((prev) => Math.max(prev - 0.5, 10));

  return (
    <div className="signature-preview">
      <h3>Предпросмотр подписи</h3>

      <div className="preview-controls">
        {/* Отображение контактов */}
        <div className="control-horizontal">
          <label className="control-group-label">Отображение контактов:</label>
          <div className="radio-group">
            <label className="radio-label">
              <input
                type="radio"
                name="display"
                value="icons"
                checked={useIcon}
                onChange={() => setUseIcon(true)}
                className="radio-input"
              />
              <span className="radio-custom"></span>
              Иконки
            </label>
            <label className="radio-label">
              <input
                type="radio"
                name="display"
                value="text"
                checked={!useIcon}
                onChange={() => setUseIcon(false)}
                className="radio-input"
              />
              <span className="radio-custom"></span>
              Текст
            </label>
          </div>
        </div>

        {/* Размер шрифта */}
        <div className="font-size-control">
          <label className="control-group-label">Размер шрифта:</label>
          <div className="font-size-buttons">
            <button onClick={increaseFont} className="font-btn">▲</button>
            <span className="font-size-value">{fontSize.toFixed(1)}pt</span>
            <button onClick={decreaseFont} className="font-btn">▼</button>
          </div>
        </div>
      </div>

      <div
        ref={signatureRef}
        dangerouslySetInnerHTML={{ __html: signatureHtml }}
        style={{
          border: "1px solid #ccc",
          padding: "15px",
          margin: "10px 0",
          backgroundColor: "#fff",
        }}
      />

      <div className="preview-actions">
        <button onClick={copyToClipboard} className="copy-btn">
          {isCopied ? "Текст скопирован" : "Скопировать подпись"}
        </button>
      </div>
    </div>
  );
};

export default SignaturePreview;